import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll } from "framer-motion";
import { Menu, X } from "lucide-react";
import { navLinks } from "../data/demoContent";
import { useGoToSection } from "../lib/useGoToSection";

const OBSERVED = ["experience", "stories", "feed", "ai", "connections", "personal-ai", "moments", "trust", "join"];

const useActiveSection = () => {
  const [active, setActive] = useState("experience");
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const id = e.target.id;
            setActive(navLinks.some((l) => l.id === id) ? id : null);
          }
        });
      },
      { rootMargin: "-45% 0px -50% 0px" }
    );
    OBSERVED.forEach((id) => {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, []);
  return active;
};

export const Nav = () => {
  const active = useActiveSection();
  const goTo = useGoToSection();
  const [open, setOpen] = useState(false);
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = (id) => {
    setOpen(false);
    goTo(id);
  };

  return (
    <header className="nav-wrap" data-testid="site-nav">
      <nav aria-label="Primary" className="glass glass-heavy nav-bar">
        <button type="button" onClick={() => go("experience")} data-testid="nav-brand-wordmark" className="nav-brand">
          SOCIAL<span className="text-[#7866E8]"> AI</span>
        </button>

        <ul className="hidden items-center gap-1 md:flex">
          {navLinks.map((l) => (
            <li key={l.id} className="relative">
              <a
                href={`#${l.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  go(l.id);
                }}
                data-testid={`nav-${l.id}-link`}
                aria-current={active === l.id ? "true" : undefined}
                className={`nav-link ${active === l.id ? "is-active" : ""}`}
              >
                {active === l.id && (
                  <motion.span layoutId="nav-active" className="nav-active-pill" transition={{ type: "spring", stiffness: 420, damping: 36 }} />
                )}
                <span className="relative">{l.label}</span>
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2">
          <button type="button" onClick={() => go("join")} data-testid="nav-join-waitlist-button" className="btn-primary hidden h-10 px-5 text-[14px] sm:inline-flex">
            Join Waitlist
          </button>
          <button
            type="button"
            className="grid h-10 w-10 place-items-center rounded-full bg-white/60 text-[#08090D] md:hidden"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-menu-button"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>
      <motion.div aria-hidden className="scroll-progress" style={{ scaleX: scrollYProgress }} data-testid="scroll-progress" />

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22 }}
            className="glass glass-heavy mt-2 rounded-[28px] p-3 md:hidden"
            data-testid="nav-mobile-menu"
          >
            {navLinks.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => go(l.id)}
                data-testid={`mobile-nav-${l.id}-link`}
                className={`block w-full rounded-2xl px-4 py-3 text-left text-[17px] font-semibold ${active === l.id ? "bg-white/70 text-[#7866E8]" : "text-[#08090D]"}`}
              >
                {l.label}
              </button>
            ))}
            <button type="button" onClick={() => go("join")} data-testid="mobile-nav-join-waitlist-button" className="btn-primary mt-2 h-12 w-full text-[15px]">
              Join Waitlist
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
